import { getXpForLevel } from '@dreamgame/shared';

interface SkillXpSample {
  readonly timestampMs: number;
  readonly xp: number;
}

interface SkillXpEntry {
  totalXp: number;
  readonly samples: SkillXpSample[];
}

const XP_RATE_WINDOW_MS = 5 * 60 * 1000;
const MIN_RATE_WINDOW_MS = 1000;

const pawnSkillXp = new Map<number, Map<string, SkillXpEntry>>();

const getOrCreatePawnSkillMap = ({
  pawnNetworkId,
}: {
  pawnNetworkId: number;
}): Map<string, SkillXpEntry> => {
  const existing = pawnSkillXp.get(pawnNetworkId);
  if (existing) {
    return existing;
  }
  const created = new Map<string, SkillXpEntry>();
  pawnSkillXp.set(pawnNetworkId, created);
  return created;
};

const getOrCreateSkillEntry = ({
  pawnNetworkId,
  skillId,
  fallbackTotalXp,
}: {
  pawnNetworkId: number;
  skillId: string;
  fallbackTotalXp: number;
}): SkillXpEntry => {
  const skillMap = getOrCreatePawnSkillMap({ pawnNetworkId });
  const existing = skillMap.get(skillId);
  if (existing) {
    return existing;
  }
  const created: SkillXpEntry = {
    totalXp: fallbackTotalXp,
    samples: [],
  };
  skillMap.set(skillId, created);
  return created;
};

const pruneSamples = ({ samples, now }: { samples: SkillXpSample[]; now: number }): void => {
  const cutoff = now - XP_RATE_WINDOW_MS;
  if (
    samples.length === 0 ||
    samples[0]?.timestampMs === undefined ||
    samples[0].timestampMs >= cutoff
  ) {
    return;
  }
  const firstValidIndex = samples.findIndex((sample) => sample.timestampMs >= cutoff);
  if (firstValidIndex <= 0) {
    samples.length = 0;
    return;
  }
  samples.splice(0, firstValidIndex);
};

export const seedSkillXpFromLevel = ({
  pawnNetworkId,
  skillId,
  level,
}: {
  pawnNetworkId: number;
  skillId: string;
  level: number;
}): void => {
  const fallbackTotalXp = getXpForLevel({ level });
  const entry = getOrCreateSkillEntry({ pawnNetworkId, skillId, fallbackTotalXp });
  if (fallbackTotalXp > entry.totalXp) {
    entry.totalXp = fallbackTotalXp;
  }
};

export const upsertSkillTotalXp = ({
  pawnNetworkId,
  skillId,
  totalXp,
}: {
  pawnNetworkId: number;
  skillId: string;
  totalXp: number;
}): void => {
  const entry = getOrCreateSkillEntry({ pawnNetworkId, skillId, fallbackTotalXp: totalXp });
  if (totalXp > entry.totalXp) {
    entry.totalXp = totalXp;
  }
};

export const recordSkillXpGain = ({
  pawnNetworkId,
  skillId,
  xpGained,
  newTotalXp,
}: {
  pawnNetworkId: number;
  skillId: string;
  xpGained: number;
  newTotalXp: number;
}): void => {
  const entry = getOrCreateSkillEntry({ pawnNetworkId, skillId, fallbackTotalXp: newTotalXp });
  if (newTotalXp > entry.totalXp) {
    entry.totalXp = newTotalXp;
  }

  if (xpGained <= 0) {
    return;
  }

  const now = performance.now();
  entry.samples.push({
    timestampMs: now,
    xp: xpGained,
  });
  pruneSamples({ samples: entry.samples, now });
};

export const getSkillTotalXp = ({
  pawnNetworkId,
  skillId,
  level,
}: {
  pawnNetworkId: number;
  skillId: string;
  level: number;
}): number => {
  const fallbackTotalXp = getXpForLevel({ level });
  const entry = pawnSkillXp.get(pawnNetworkId)?.get(skillId);
  if (!entry) {
    return fallbackTotalXp;
  }
  return Math.max(entry.totalXp, fallbackTotalXp);
};

export const estimateSecondsToNextLevel = ({
  pawnNetworkId,
  skillId,
  xpRemaining,
}: {
  pawnNetworkId: number;
  skillId: string;
  xpRemaining: number;
}): number | null => {
  if (xpRemaining <= 0) {
    return 0;
  }

  const entry = pawnSkillXp.get(pawnNetworkId)?.get(skillId);
  if (!entry || entry.samples.length === 0) {
    return null;
  }

  const now = performance.now();
  pruneSamples({ samples: entry.samples, now });
  if (entry.samples.length === 0) {
    return null;
  }

  let xpInWindow = 0;
  let oldestTimestamp = now;

  for (const sample of entry.samples) {
    xpInWindow += sample.xp;
    if (sample.timestampMs < oldestTimestamp) {
      oldestTimestamp = sample.timestampMs;
    }
  }

  if (xpInWindow <= 0) {
    return null;
  }

  const elapsedMs = Math.max(MIN_RATE_WINDOW_MS, now - oldestTimestamp);
  const xpPerSecond = xpInWindow / (elapsedMs / 1000);
  if (xpPerSecond <= 0) {
    return null;
  }

  return xpRemaining / xpPerSecond;
};

export const clearSkillXpState = (): void => {
  pawnSkillXp.clear();
};
